import { useState } from "react";
import { Download, X, Filter, Loader2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { isAdmin, hasGlobalAccess } from "../types";
import toast from "react-hot-toast";

type AssessmentType =
  | "sdl"
  | "cra-applicability"
  | "cra-compliance"
  | "data-act";

interface AssessmentExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  periods: { year: number; quarter: number; period: string }[];
  businessAreas: string[];
  divisions: string[];
  initialProductName?: string;
}

const ASSESSMENT_TYPES: {
  value: AssessmentType;
  label: string;
  description: string;
}[] = [
  {
    value: "sdl",
    label: "SDL Assessment",
    description:
      "Security Development Lifecycle maturity scores and question details",
  },
  {
    value: "cra-applicability",
    label: "CRA Applicability",
    description: "CRA scope determination results and answers",
  },
  {
    value: "cra-compliance",
    label: "CRA Compliance",
    description: "CRA compliance survey answers and evidence",
  },
  {
    value: "data-act",
    label: "Data Act Assessment",
    description:
      "Data Act scope determination, classifications and questionnaire answers",
  },
];

export default function AssessmentExportModal({
  isOpen,
  onClose,
  periods,
  businessAreas,
  divisions,
  initialProductName,
}: AssessmentExportModalProps) {
  const { user } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [assessmentType, setAssessmentType] = useState<AssessmentType>("sdl");

  // Filters
  const [period, setPeriod] = useState("");
  const [businessArea, setBusinessArea] = useState("");
  const [division, setDivision] = useState("");
  const [productName, setProductName] = useState(initialProductName ?? "");
  const [ownerName, setOwnerName] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // SDL-specific options
  const [includePracticeScores, setIncludePracticeScores] = useState(true);
  const [includeQuestionDetails, setIncludeQuestionDetails] = useState(true);

  if (!isOpen) return null;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (businessArea) params.set("businessArea", businessArea);
      if (division) params.set("division", division);
      if (productName) params.set("productName", productName);
      if (ownerName) params.set("ownerName", ownerName);
      if (status) params.set("status", status);

      if (assessmentType === "sdl") {
        if (period) params.set("period", period);
        if (dateFrom) params.set("dateFrom", dateFrom);
        if (dateTo) params.set("dateTo", dateTo);

        const columns: string[] = [
          "productName",
          "businessArea",
          "division",
          "owner",
          "ownerEmail",
          "productOwner",
          "productOwnerEmail",
          "period",
          "status",
          "overallScore",
          "assessedBy",
          "completedAt",
          "updatedAt",
        ];
        if (includePracticeScores) {
          [
            "Security Requirements",
            "Secure Design",
            "Secure Implementation",
            "Security Testing",
            "Security Deployment",
            "Security Operations",
            "Security Governance",
          ].forEach((p) => columns.push("practice_" + p));
        }
        if (includeQuestionDetails) {
          for (let i = 1; i <= 25; i++) {
            columns.push(`q${i}_score`, `q${i}_details`, `q${i}_evidence`);
          }
        }
        params.set("columns", columns.join(","));
      }

      let endpoint = "/api/assessments/sdl/export";
      if (assessmentType === "cra-applicability") {
        endpoint = "/api/assessments/cra/export";
      } else if (assessmentType === "cra-compliance") {
        endpoint = "/api/assessments/cra-compliance/export";
      } else if (assessmentType === "data-act") {
        endpoint = "/api/assessments/data-act/export";
      }

      const token = localStorage.getItem("token");
      const response = await fetch(`${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = response.headers.get("Content-Disposition");
      const filename =
        disposition?.match(/filename=(.+)/)?.[1] ||
        `${assessmentType}-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Export downloaded successfully");
      onClose();
    } catch {
      toast.error("Failed to export assessments");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Download className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Export Assessments
              </h2>
              <p className="text-sm text-gray-500">
                Download assessment data as Excel spreadsheet
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Assessment Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assessment Type
            </label>
            <div className="grid grid-cols-1 gap-2">
              {ASSESSMENT_TYPES.map((type) => (
                <label
                  key={type.value}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    assessmentType === type.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="assessmentType"
                    value={type.value}
                    checked={assessmentType === type.value}
                    onChange={() => setAssessmentType(type.value)}
                    className="mt-0.5 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {type.label}
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {type.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              <Filter className="h-4 w-4" />
              Filters
              <span className="text-xs text-gray-400">
                ({showFilters ? "hide" : "show"})
              </span>
            </button>

            {showFilters && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {assessmentType === "sdl" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Period
                    </label>
                    <select
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">All periods</option>
                      {periods.map((p) => (
                        <option key={p.period} value={p.period}>
                          {p.period} (Q{p.quarter} {p.year})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(assessmentType === "sdl" ||
                  assessmentType === "cra-compliance" ||
                  assessmentType === "data-act") && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">All</option>
                      <option value="submitted">Submitted</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                )}

                {(hasGlobalAccess(user) || isAdmin(user)) && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Business Area
                      </label>
                      <select
                        value={businessArea}
                        onChange={(e) => setBusinessArea(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">All</option>
                        {businessAreas.map((ba) => (
                          <option key={ba} value={ba}>
                            {ba}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Division/Business Unit
                      </label>
                      <select
                        value={division}
                        onChange={(e) => setDivision(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">All</option>
                        {divisions.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Search..."
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Owner Name
                  </label>
                  <input
                    type="text"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="Search..."
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                {assessmentType === "sdl" && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Date From
                      </label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Date To
                      </label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* SDL column options */}
          {assessmentType === "sdl" && (
            <div>
              <span className="text-sm font-medium text-gray-700 block mb-2">
                Include in Export
              </span>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includePracticeScores}
                    onChange={() =>
                      setIncludePracticeScores(!includePracticeScores)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Practice Scores (per-practice maturity scores)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeQuestionDetails}
                    onChange={() =>
                      setIncludeQuestionDetails(!includeQuestionDetails)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Individual Question Scores, Details &amp; Evidence (25
                  questions × 3 columns)
                </label>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> The export is filtered based on your role
              and access level. You will only see products within your scope.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export to Excel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
